import { addSheetBackLink, PARENT_SHEETS } from "../sheetNavigation.js";
import {
  BACK_ROW,
  BODY_FONT,
  NA_FILL,
  VALUE_FILL,
  hideRowsThrough,
  setCell,
  setupSheetView,
  applyColumnLayout,
} from "./detailSheetCommon.js";
import {
  COL_D,
  COL_E,
  COL_G,
  addSheetTitle,
  applyBorderPattern,
  outerRowPatterns,
} from "./principle2DetailHelpers.js";
import {
  formatDischarge,
  formatNumber,
  getFyLabels,
  readFY,
  safeText,
  sumDischargeSources,
  sumFYCells,
} from "./principle6SheetHelpers.js";

export const SHEET_NAME = "Plant in area of water stress";

const SHEET_TITLE =
  "Details For each facility / plant located in areas of water stress";

const WIDTHS = {
  A: 20.7265625,
  B: 20.7265625,
  C: 9.1796875,
  D: 9.1796875,
  E: 63.81640625,
  F: 25.453125,
  G: 25.453125,
  H: 25.453125,
  I: 25.453125,
  J: 25.453125,
  K: 25.453125,
  L: 25.453125,
  M: 25.453125,
  N: 12.453125,
};

const TEMPLATE_ROW = 9;
const SPACER_ROW = 10;
const DATA_COL_START = 6; // F
const COLS_PER_FACILITY = 2;

const WITHDRAWAL_KEYS = [
  "surfaceWater",
  "groundWater",
  "thirdPartyWater",
  "seaWater",
  "other",
];

const DISCHARGE_KEYS = [
  "toSurfaceWater",
  "toGroundwater",
  "toProducedWater",
  "toThirdParties",
  "others",
];

const LABEL_ROWS = [
  { row: 11, sr: "Sr. No.", label: "Particulars", greyD: true, greyE: true },
  { row: 12, sr: "1", label: "Name of the area " },
  { row: 13, sr: "2", label: "Nature of operations " },
  {
    row: 14,
    sr: "3",
    label: "Water withdrawal, consumption and discharge in the following format:",
    greyD: true,
    greyE: true,
  },
  { row: 15, label: "Parameter", greyE: true },
  { row: 16, label: "Water withdrawal by source (in kilolitres)", greyE: true },
  { row: 17, label: "(i) Surface water" },
  { row: 18, label: "(ii) Groundwater" },
  { row: 19, label: "(iii) Third party water" },
  { row: 20, label: "(iv) Seawater / desalinated water" },
  { row: 21, label: "(v) Others" },
  { row: 22, label: "Total volume of water withdrawal\n(in kilolitres)", height: 15 },
  { row: 23, label: "Total volume of water consumption (in kilolitres)" },
  {
    row: 24,
    label: "Water intensity per rupee of turnover (Water consumed / turnover)",
    height: 30,
  },
  {
    row: 25,
    label:
      "Water intensity (optional) – the relevant metric may be selected by the entity",
    height: 29,
  },
  {
    row: 26,
    label:
      "Water discharge by destination and level of treatment (in kilolitres)",
    greyE: true,
    height: 30,
  },
  { row: 27, label: "(i) Into Surface water" },
  { row: 28, label: "No treatment" },
  { row: 29, label: "With treatment – please specify level of treatment" },
  { row: 30, label: "(ii) Into Groundwater" },
  { row: 31, label: "No treatment" },
  { row: 32, label: "With treatment – please specify level of treatment" },
  { row: 33, label: "(iii) Into Seawater" },
  { row: 34, label: "No treatment" },
  { row: 35, label: "With treatment – please specify level of treatment" },
  { row: 36, label: "(iv) Sent to third-parties" },
  { row: 37, label: "No treatment" },
  { row: 38, label: "With treatment – please specify level of treatment" },
  { row: 39, label: "(v) Others" },
  { row: 40, label: "No treatment" },
  { row: 41, label: "With treatment – please specify level of treatment" },
  { row: 42, label: "Total water discharged (in kilolitres)" },
];

function thin() {
  return { style: "thin", color: { argb: "FF000000" } };
}

function thinBorder() {
  return { top: thin(), left: thin(), bottom: thin(), right: thin() };
}

/** Keep a visible right edge on a merged range (ExcelJS often drops it). */
function ensureMergedOuterBorder(ws, startRow, startCol, endRow, endCol) {
  const master = ws.getCell(startRow, startCol);
  const prev = master.border || {};
  master.border = {
    top: prev.top || thin(),
    left: prev.left || thin(),
    bottom: prev.bottom || thin(),
    right: thin(),
  };
  // Also stamp the right edge onto the last column cells so Excel keeps it.
  for (let row = startRow; row <= endRow; row += 1) {
    const edge = ws.getCell(row, endCol);
    const edgePrev = edge.border || {};
    edge.border = {
      top: edgePrev.top || thin(),
      left: edgePrev.left,
      bottom: edgePrev.bottom || thin(),
      right: thin(),
    };
  }
}

function ensureColumnGRightBorder(ws, fromRow, toRow) {
  for (let row = fromRow; row <= toRow; row += 1) {
    const cell = ws.getCell(row, COL_G);
    const prev = cell.border || {};
    cell.border = {
      top: prev.top,
      left: prev.left,
      bottom: prev.bottom,
      right: thin(),
    };
  }
}

function facilityStartCol(index) {
  return DATA_COL_START + index * COLS_PER_FACILITY;
}

function setGridCell(ws, row, col, value, { fill, align = "left" } = {}) {
  setCell(ws, row, col, value ?? "", {
    font: BODY_FONT,
    bold: false,
    bordered: false,
    border: thinBorder(),
    align,
    fill,
  });
}

function withTreatmentValue(block) {
  const amount = formatDischarge(block?.dischargeAfterTreatment);
  const level = safeText(block?.treatmentLevel ?? block?.treatMentLeval, "");
  if (amount && level && amount !== "-" && level !== "-") return `${amount} (${level})`;
  if (amount && amount !== "-") return amount;
  if (level && level !== "-") return level;
  return "";
}

function writeFacilityColumns(ws, facility, colStart, fyCurrent, fyPrevious) {
  const colCurr = colStart;
  const colPrev = colStart + 1;
  const w = facility?.waterWithdrawal || {};

  setGridCell(ws, 12, colCurr, safeText(facility?.nameOfArea, ""), {
    fill: VALUE_FILL,
    align: "center",
  });
  setGridCell(ws, 12, colPrev, "", { fill: VALUE_FILL, align: "center" });
  ws.mergeCells(12, colCurr, 12, colPrev);
  ensureMergedOuterBorder(ws, 12, colCurr, 12, colPrev);

  setGridCell(ws, 13, colCurr, safeText(facility?.operation, ""), {
    fill: VALUE_FILL,
    align: "center",
  });
  setGridCell(ws, 13, colPrev, "", { fill: VALUE_FILL, align: "center" });
  ws.mergeCells(13, colCurr, 13, colPrev);
  ensureMergedOuterBorder(ws, 13, colCurr, 13, colPrev);

  for (const row of [14, 16, 26]) {
    setGridCell(ws, row, colCurr, "", { fill: NA_FILL, align: "center" });
    setGridCell(ws, row, colPrev, "", { fill: NA_FILL, align: "center" });
  }
  setGridCell(ws, 15, colCurr, fyCurrent, { fill: NA_FILL, align: "center" });
  setGridCell(ws, 15, colPrev, fyPrevious, { fill: NA_FILL, align: "center" });

  const withdrawalRows = [
    [17, "surfaceWater"],
    [18, "groundWater"],
    [19, "thirdPartyWater"],
    [20, "seaWater"],
    [21, "other"],
  ];
  withdrawalRows.forEach(([row, key]) => {
    setGridCell(ws, row, colCurr, formatNumber(readFY(w[key], "current")), {
      fill: VALUE_FILL,
      align: "center",
    });
    setGridCell(ws, row, colPrev, formatNumber(readFY(w[key], "previous")), {
      fill: VALUE_FILL,
      align: "center",
    });
  });

  setGridCell(
    ws,
    22,
    colCurr,
    formatNumber(sumFYCells(w, WITHDRAWAL_KEYS, "current") ?? readFY(w.total, "current")),
    { fill: VALUE_FILL, align: "center" }
  );
  setGridCell(
    ws,
    22,
    colPrev,
    formatNumber(sumFYCells(w, WITHDRAWAL_KEYS, "previous") ?? readFY(w.total, "previous")),
    { fill: VALUE_FILL, align: "center" }
  );

  [
    [23, "totalWaterConsumption"],
    [24, "waterIntensityPerRupee"],
    [25, "waterIntensity"],
  ].forEach(([row, key]) => {
    setGridCell(ws, row, colCurr, formatNumber(readFY(w[key], "current")), {
      fill: VALUE_FILL,
      align: "center",
    });
    setGridCell(ws, row, colPrev, formatNumber(readFY(w[key], "previous")), {
      fill: VALUE_FILL,
      align: "center",
    });
  });

  for (const row of [27, 30, 33, 36, 39]) {
    setGridCell(ws, row, colCurr, "", { fill: null, align: "center" });
    setGridCell(ws, row, colPrev, "", { fill: null, align: "center" });
  }

  [
    [28, 29, "toSurfaceWater"],
    [31, 32, "toGroundwater"],
    [34, 35, "toProducedWater"],
    [37, 38, "toThirdParties"],
    [40, 41, "others"],
  ].forEach(([noRow, withRow, key]) => {
    const c = facility?.waterDischargeCurrent?.[key] || {};
    const p = facility?.waterDischargePrevious?.[key] || {};
    setGridCell(ws, noRow, colCurr, formatDischarge(c.dischargeWithoutTreatment), {
      fill: VALUE_FILL,
      align: "center",
    });
    setGridCell(ws, noRow, colPrev, formatDischarge(p.dischargeWithoutTreatment), {
      fill: VALUE_FILL,
      align: "center",
    });
    setGridCell(ws, withRow, colCurr, withTreatmentValue(c), {
      fill: VALUE_FILL,
      align: "center",
    });
    setGridCell(ws, withRow, colPrev, withTreatmentValue(p), {
      fill: VALUE_FILL,
      align: "center",
    });
  });

  setGridCell(
    ws,
    42,
    colCurr,
    formatDischarge(
      sumDischargeSources(facility?.waterDischargeCurrent, DISCHARGE_KEYS) ??
        facility?.waterDischargeCurrent?.total
    ),
    { fill: VALUE_FILL, align: "center" }
  );
  setGridCell(
    ws,
    42,
    colPrev,
    formatDischarge(
      sumDischargeSources(facility?.waterDischargePrevious, DISCHARGE_KEYS) ??
        facility?.waterDischargePrevious?.total
    ),
    { fill: VALUE_FILL, align: "center" }
  );
}

export function buildPlantInAreaOfWaterStressSheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet(SHEET_NAME, { views: [{ showGridLines: false }] });
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);
  const facilities =
    sectionC?.principle6?.leadershipIndicator?.waterWithDrawalConsumptionAndDischarg || [];
  const dataRows = Array.isArray(facilities) && facilities.length ? facilities : [{}];

  applyColumnLayout(ws, WIDTHS, ["A", "B"]);
  hideRowsThrough(ws, 4);
  setupSheetView(ws, {
    ySplit: 11,
    xSplit: 5,
    topLeftCell: "F12",
    zoomScale: 80,
  });

  addSheetBackLink(ws, PARENT_SHEETS.PRINCIPLE_6, {
    row: BACK_ROW,
    col: COL_G,
    mergeEnd: COL_G,
    align: "right",
    parentCell: "D8",
  });

  addSheetTitle(ws, COL_G, SHEET_TITLE, {
    borderPatterns: outerRowPatterns(COL_D, COL_G),
  });
  ensureMergedOuterBorder(ws, 8, COL_D, 8, COL_G);

  applyBorderPattern(ws, TEMPLATE_ROW, COL_D, ["-t--", "----", "----", "---t"]);
  for (let col = COL_D; col <= COL_G; col += 1) {
    setCell(ws, TEMPLATE_ROW, col, "", { bordered: false, font: BODY_FONT });
  }
  ws.getRow(TEMPLATE_ROW).hidden = true;

  applyBorderPattern(ws, SPACER_ROW, COL_D, ["-tt-", "--t-", "--t-", "--tt"]);
  for (let col = COL_D; col <= COL_G; col += 1) {
    setCell(ws, SPACER_ROW, col, "", { bordered: false, font: BODY_FONT });
  }
  ws.mergeCells(SPACER_ROW, COL_D, SPACER_ROW, COL_G);
  ensureMergedOuterBorder(ws, SPACER_ROW, COL_D, SPACER_ROW, COL_G);
  ws.getRow(SPACER_ROW).height = 33;

  LABEL_ROWS.forEach(({ row, sr, label, greyD, greyE, height }) => {
    setGridCell(ws, row, COL_D, sr ?? "", {
      fill: greyD ? NA_FILL : undefined,
      align: "center",
    });
    setGridCell(ws, row, COL_E, label, {
      fill: greyE ? NA_FILL : undefined,
      align: row === 11 ? "center" : "left",
    });
    if (height) ws.getRow(row).height = height;
  });

  dataRows.forEach((facility, idx) => {
    const colStart = facilityStartCol(idx);
    ws.getColumn(colStart).width = 25.453125;
    ws.getColumn(colStart + 1).width = 25.453125;
    writeFacilityColumns(ws, facility, colStart, fyCurrent, fyPrevious);
  });

  // Keep a continuous right edge on column G (title + first facility PY column).
  ensureColumnGRightBorder(ws, 8, 42);

  return ws;
}
